import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { Berth } from 'data/models/Berth';

import { ManageBerths, PageMode, PortViewModel, PierGroupViewModel, BerthViewModel } from './model';

import { PCSortable } from 'portcall/pcsortable/view';
import { PierGroupFootmarksView } from 'portcall/piergroupfootmarks/view';

export const ManageBerthsView = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="manageberths pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href="#!siteadminsettings">&lt;&lt; Back to Site Admin Settings</a>
        </div>
        <h1 className="pc-page-title">
            <span className="pc-text-label-color">More</span><span className="pc-icon-bullet pc-text-label-color"></span>
            <span className="pc-text-label-color">Site Admin Settings</span><span className="pc-icon-bullet"></span>
            <span>Manage Berths</span>
        </h1>
        <PageBody model={m} />
    </div>
);

const PageBody = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="page-body pc-card">
        <PageBodyTop model={m} />
        <PageBodyLeft model={m} />
        <PageBodyRight model={m} />
    </div>
);

const PageBodyTop = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="page-body-top">
        <h2 className="pc-card-title page-body-title">Berths</h2>
        <div className="port-filter">
            <select className="pc-select-secondary"
                value={m.SelectedPortId() || ''}
                onChange={e => m.SelectedPortId(e.currentTarget.value ? +e.currentTarget.value : null)}>
                <option value="">--</option>
                {m.Ports().map(p =>
                    <option key={p.Model.Id()} value={p.Model.Id()}>{p.Model.Name()}</option>
                )}
            </select>
            <span className="pc-select-chevron"></span>
        </div>
    </div>
);

const PageBodyLeft = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="left-side">
        <ul className="ports-in-domain">
            {m.Ports().map(p =>
                <PortRow port={p} model={m} key={p.Model.Id()} />
            )}
        </ul>
    </div>
);

const PortRow = SuperCritical(({ port: p, model: m } : { port : PortViewModel, model : ManageBerths }) => {
    if (!p.Show()) return null;
    return (
        <li>
            <div className="port-header">{p.Model.Name()}</div>
            <PCSortable
                className="piergroups-in-port"
                onSort={p.OnSortPierGroups}
                items={p.PierGroups}
                rowFactory={pg =>
                    <PierGroupRow pierGroup={pg} model={m} key={pg.Model.Id()} />
                }
                disabled={() => m.PageMode() !== PageMode.Arrange}
            />
        </li>
    );
});

const PierGroupRow = SuperCritical(({ pierGroup: pg, model: m } : { pierGroup : PierGroupViewModel, model : ManageBerths }) =>
    <li className={cx({
            'cursor-auto': m.PageMode() !== PageMode.Arrange
        })}>
        <a href="" className="unlink-btn"
            hidden={m.PageMode() !== PageMode.Link || pg.Berths().length === 1}
            onClick={noDefault(pg.Unlink)}>Unlink</a>
        <ul className="berths-in-piergroup">
            {pg.Berths().map(b =>
                <BerthRow berth={b} pierGroup={pg} model={m} key={b.Model.Id()} />
            )}
        </ul>
    </li>
);

const BerthRow = SuperCritical(({ berth: b, pierGroup: pg, model: m } : { berth : BerthViewModel, pierGroup : PierGroupViewModel, model : ManageBerths }) =>
    <li className={cx({
            'cursor-click': m.PageMode() === PageMode.View,
            'cursor-auto':  m.PageMode() !== PageMode.View && m.PageMode() !== PageMode.Arrange
        })}
        onClick={m.PageMode() === PageMode.View ? b.NavigateToProfile : undefined }>
        <span className="berth-name">{b.Model.Location() && b.Model.Location()!.Name()}</span>
        <input className="link-btn" type="checkbox"
            checked={b.SelectedForLinking()}
            onChange={e => b.SelectedForLinking(e.currentTarget.checked)}
            hidden={m.PageMode() !== PageMode.Link || pg.Berths().length > 1} />
    </li>
);

const PageBodyRight = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="right-side">
        <div className="page-description">
            This is a list of all the berths and piers in the system. Berths will appear in this configuration throughout the site, such as 
            on the Berths Timeline. Select a row to jump to that berth's profile.
        </div>
        <ActionsView model={m} />
        <EditLinkedBerthsView model={m} />
    </div>
);

const ActionsView = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="pc-card right-side-card actions-box">
        <div className="pc-card-header pc-card-title">Actions</div>
        <ArrangeLink model={m} />
        <LinkBerthsLink model={m} />
        <AddBerthLink model={m} />
        <RemoveBerthLink model={m} />
    </div>
);

const ArrangeLink = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className={cx('action', { selected: m.PageMode() === PageMode.Arrange })}>
        <a href="" className="done-link" onClick={noDefault(() => m.PageMode(PageMode.View))}
            hidden={m.PageMode() !== PageMode.Arrange}>Done</a>
        <a href="" className="main-link" onClick={noDefault(() => m.PageMode(PageMode.Arrange))}>
            Arrange Berth Order
        </a>
        <div className="description">
            Drag berths into regions.
        </div>
    </div>
);

const LinkBerthsLink = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className={cx('action', { selected: m.PageMode() === PageMode.Link})}>
        <a href="" className="done-link" onClick={noDefault(() => m.PageMode(PageMode.View))}
            hidden={m.PageMode() !== PageMode.Link}>Done</a>
        <a href="" className="main-link" onClick={noDefault(() => m.PageMode(PageMode.Link))}>
            Link Berths
        </a>
        <div className="description">
            Link two or more berths together so vessels can be scheduled across their total length. Select the berths, 
            arrange them, and then Link them.
        </div>
    </div>
);

const AddBerthLink = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="action">
        <a href="" className="main-link" onClick={noDefault(m.AddBerth.Add)}>Add Berth</a>
        <div className="description">
            Create a new berth and edit that berth's profile. Enter its name, draw its zone, and fill in the appropriate 
            fields.
        </div>
    </div>
);

const RemoveBerthLink = SuperCritical(({ model: m } : { model : ManageBerths }) =>
    <div className="action">
        <a href="" onClick={noDefault(() => {/* TH 12/18/2016 -- Still TODO... */})}>Remove Berth</a>
        <div className="description">
            Remove a berth from the system. A historical record will remain for billing purposes.
        </div>
    </div>
);

const EditLinkedBerthsView = SuperCritical(({ model: m } : { model : ManageBerths }) => {
    if (m.PageMode() !== PageMode.Link) return null;
    const p = m.EditLinkedBerths.PortWithSelectedBerths();
    return p &&
        <div className="edit-linked-berths-order">
            {!m.EditLinkedBerths.InvalidBerthSelection() && p.SelectedBerths.CanLink() &&
                <a href="" className="confirm" onClick={noDefault(p.SelectedBerths.Link)}>Link Berths</a>
            }
            <h3 className="section-title">Edit Order</h3>
            {m.EditLinkedBerths.InvalidBerthSelection() ?
                <div className="invalid-linked-berths">
                    You cannot link berths from multiple ports. Please make another selection.
                </div> :
                <PierGroupFootmarksView model={p.SelectedBerths.NewPierGroupFootmarks} />
            }
        </div>;
});



// WEBPACK FOOTER //
// ./apps/portcall/manageberths/view.tsx